/** @graphql experimental **/

import { useCallback } from 'react';
import type { ApolloError } from 'apollo-client';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { PlatformSiteAri } from '@atlassian/ari';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE } from '../Controller.tsx';

import { useExperimentalInvitePermissionsQuery } from './useExperimentalInvitePermissionsQuery.ts';

export const useInviteCapability = () => {
	const { cloudId: siteId } = useSessionData();
	const resourceARI = PlatformSiteAri.create({ siteId }).toString();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onFetchCapabilitiesCompleted = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
				action: 'success',
				actionSubject: 'useInviteCapabilityShareAndRestrictDialogQuery',
			},
		}).fire();
	}, [createAnalyticsEvent]);

	const onFetchCapabilitiesError = useCallback(
		(queryError: ApolloError) => {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
					action: 'error',
					actionSubject: 'useInviteCapabilityShareAndRestrictDialogQuery',
					attributes: {
						error: queryError.message,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	const [loadInviteCapabilities, { data, loading, error, called }] =
		useExperimentalInvitePermissionsQuery({
			variables: { resourceARI },
			onCompleted: onFetchCapabilitiesCompleted,
			onError: onFetchCapabilitiesError,
		});

	const canUserInvite = useCallback(() => {
		if (!data || loading || error) {
			return false;
		}
		return data.experimentalInvitePermissions?.invitePendingApprovalMode === 'ANYONE';
	}, [data, loading, error]);

	return {
		loadInviteCapabilities,
		canUserInvite,
		called,
	};
};
